import { useCallback, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import {
  type TableColumn,
  type TableProps,
  useDataGrid,
} from "@alpacahq/ui/old/datagrid";

import {
  useCryptoWalletAPI,
  useGetCryptoAssets,
  useGetCryptoWhitelists,
} from "~/v2/api/hooks/useGetCrypto";
import { queryKeys } from "~/v2/api/queryKeys";
import type { CryptoWhitelistedAddress } from "~/v2/api/rest/cryptoWallet";
import { Path } from "~/v2/path";
import { createAssetMap } from "../util";
import {
  type UseAddressDetailsReturn,
  useAddressDetails,
} from "./useAddressDetails";
import { createWhitelistColumns } from "./whitelistColumns";

export interface UseWhitelistTableControllerProps {
  accountId: string;
  isLoading: boolean;
  isFetchingEnabled: boolean;
  isTRCEnabled: boolean;
  whitelistAddressPath?: string;
  onAddWhitelistAddressClicked: () => void;
}

export interface WhitelistTableControllerReturn {
  whitelist: {
    data: CryptoWhitelistedAddress[];
    tableProps: TableProps<CryptoWhitelistedAddress>;
    isLoading: boolean;
    refetch: () => void;
    detailsModal: UseAddressDetailsReturn["detailsModal"];
  };
  onAddWhitelistAddressClicked: () => void;
}

export const useWhitelistTableController = ({
  accountId,
  isLoading,
  isFetchingEnabled,
  isTRCEnabled,
  whitelistAddressPath = Path.ROUTE_CRYPTO_WHITELIST_ADDRESS,
  onAddWhitelistAddressClicked,
}: UseWhitelistTableControllerProps): WhitelistTableControllerReturn => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const cryptoWalletAxiosInstance = useCryptoWalletAPI();

  const assets = useGetCryptoAssets({
    accountId,
    options: {
      enabled: isFetchingEnabled,
    },
  });

  const whitelists = useGetCryptoWhitelists({
    accountId,
    options: {
      enabled: isFetchingEnabled,
    },
  });

  const whitelistsTableRows = useMemo(
    () => whitelists.data ?? [],
    [whitelists.data],
  );

  const assetMap = useMemo(
    () => createAssetMap(assets.data ?? []),
    [assets.data],
  );

  const invalidateWhitelistCaches = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crypto.whitelists(accountId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crypto.approvedWhitelists(accountId),
    });
  }, [queryClient, accountId]);

  const addressDetails = useAddressDetails({
    cryptoWalletAxiosInstance,
    accountId,
    refetchWhitelist: invalidateWhitelistCaches,
    assetMap,
  });

  const onUpdateClicked = useCallback(
    (address: CryptoWhitelistedAddress) => {
      history.push({
        pathname: whitelistAddressPath,
        search: `?whitelistId=${address.id}`,
      });
    },
    [history, whitelistAddressPath],
  );

  const whitelistsTableColumns: TableColumn<CryptoWhitelistedAddress>[] =
    useMemo(
      () =>
        createWhitelistColumns({
          assetMap,
          isTRCEnabled,
          onUpdateClicked,
          onDetailsClicked: addressDetails.onDetailsClicked,
        }),
      [
        assetMap,
        isTRCEnabled,
        onUpdateClicked,
        addressDetails.onDetailsClicked,
      ],
    );

  const whitelistsTableProps = useDataGrid({
    rows: whitelistsTableRows,
    columns: whitelistsTableColumns,
  });

  return {
    whitelist: {
      data: whitelists.data ?? [],
      tableProps: whitelistsTableProps,
      isLoading: whitelists.isLoading || isLoading,
      refetch: whitelists.refetch,
      detailsModal: addressDetails.detailsModal,
    },
    onAddWhitelistAddressClicked,
  };
};
